import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { useUpdateSeed } from '@/hooks/use-seeds';
import { toast } from 'sonner';
import { toToastMessage } from '@/lib/api-error';
import type { PracticeSeedItem, ReportMode, ReportMediaType } from '@/types/seeds';

interface EditSeedDialogProps {
  seed: PracticeSeedItem;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  categories: string[];
}

export function EditSeedDialog({ seed, open, onOpenChange, categories }: EditSeedDialogProps) {
  const mutation = useUpdateSeed();

  const [formData, setFormData] = useState({
    problem_category: seed.problem_category,
    seed_number: String(seed.seed_number),
    title: seed.title,
    description: seed.description,
    estimated_minutes: String(seed.estimated_minutes),
    difficulty_level: String(seed.difficulty_level),
    report_mode: (seed.report_mode ?? 'none') as ReportMode,
    report_approval_required: seed.report_approval_required ?? false,
    report_allowed_media: (seed.report_allowed_media ?? ['text']) as ReportMediaType[],
    report_prompt: seed.report_prompt ?? '',
  });

  useEffect(() => {
    setFormData({
      problem_category: seed.problem_category,
      seed_number: String(seed.seed_number),
      title: seed.title,
      description: seed.description,
      estimated_minutes: String(seed.estimated_minutes),
      difficulty_level: String(seed.difficulty_level),
      report_mode: (seed.report_mode ?? 'none') as ReportMode,
      report_approval_required: seed.report_approval_required ?? false,
      report_allowed_media: (seed.report_allowed_media ?? ['text']) as ReportMediaType[],
      report_prompt: seed.report_prompt ?? '',
    });
  }, [seed]);

  const toggleMedia = (m: ReportMediaType) => {
    setFormData((p) => {
      const has = p.report_allowed_media.includes(m);
      return {
        ...p,
        report_allowed_media: has
          ? p.report_allowed_media.filter((x) => x !== m)
          : [...p.report_allowed_media, m],
      };
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate(
      {
        id: seed.id,
        input: {
          problem_category: formData.problem_category,
          seed_number: parseInt(formData.seed_number),
          title: formData.title,
          description: formData.description,
          estimated_minutes: parseInt(formData.estimated_minutes),
          difficulty_level: parseInt(formData.difficulty_level),
          report_mode: formData.report_mode,
          report_approval_required: formData.report_approval_required,
          report_allowed_media: formData.report_allowed_media,
          report_prompt: formData.report_prompt || undefined,
        },
      },
      {
        onSuccess: () => {
          toast.success('Seed updated successfully');
          onOpenChange(false);
        },
        onError: (err) => {
          toast.error(toToastMessage(err, 'Failed to update seed'));
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Practice Seed</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Category</Label>
            <Select
              value={formData.problem_category}
              onValueChange={(v) => setFormData((p) => ({ ...p, problem_category: v ?? '' }))}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Seed Number</Label>
              <Input
                type="number"
                min={1}
                value={formData.seed_number}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setFormData((p) => ({ ...p, seed_number: e.target.value }))
                }
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Difficulty (1-5)</Label>
              <Select
                value={formData.difficulty_level}
                onValueChange={(v) => setFormData((p) => ({ ...p, difficulty_level: v ?? '1' }))}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5].map((d) => (
                    <SelectItem key={d} value={String(d)}>
                      {d}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Title</Label>
            <Input
              value={formData.title}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setFormData((p) => ({ ...p, title: e.target.value }))
              }
              required
            />
          </div>

          <div className="space-y-2">
            <Label>Description</Label>
            <textarea
              rows={3}
              className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              value={formData.description}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setFormData((p) => ({ ...p, description: e.target.value }))
              }
              required
            />
          </div>

          <div className="space-y-2">
            <Label>Estimated Minutes</Label>
            <Input
              type="number"
              min={1}
              max={60}
              value={formData.estimated_minutes}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setFormData((p) => ({ ...p, estimated_minutes: e.target.value }))
              }
              required
            />
          </div>

          {/* SPEC-015: Report configuration */}
          <div className="space-y-3 rounded-md border p-3">
            <Label className="text-sm font-semibold">Report</Label>
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Mode</Label>
              <div className="flex gap-4">
                {(['none', 'optional', 'required'] as ReportMode[]).map((mode) => (
                  <label key={mode} className="flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      name="report_mode"
                      value={mode}
                      checked={formData.report_mode === mode}
                      onChange={() => setFormData((p) => ({ ...p, report_mode: mode }))}
                    />
                    <span className="capitalize">{mode}</span>
                  </label>
                ))}
              </div>
            </div>

            {formData.report_mode !== 'none' && (
              <>
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Allowed media</Label>
                  <div className="flex flex-wrap gap-4">
                    {(['text', 'photo', 'video'] as ReportMediaType[]).map((m) => (
                      <label key={m} className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={formData.report_allowed_media.includes(m)}
                          onChange={() => toggleMedia(m)}
                        />
                        <span className="capitalize">{m}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={formData.report_approval_required}
                    onChange={(e) =>
                      setFormData((p) => ({ ...p, report_approval_required: e.target.checked }))
                    }
                  />
                  Require teacher/admin approval
                </label>

                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Prompt (shown to student)</Label>
                  <textarea
                    rows={2}
                    maxLength={500}
                    className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={formData.report_prompt}
                    onChange={(e) =>
                      setFormData((p) => ({ ...p, report_prompt: e.target.value }))
                    }
                    placeholder="e.g. Send a photo of your pose from the side"
                  />
                  <p className="text-right text-xs text-muted-foreground">
                    {formData.report_prompt.length}/500
                  </p>
                </div>
              </>
            )}
          </div>

          <Button type="submit" className="w-full" disabled={mutation.isPending}>
            {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Changes
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
